import React, { useState, useEffect, useRef } from 'react';
import { Avatar, Skeleton, SxProps, Theme } from '@mui/material';

interface PreloadedAvatarProps {
  src: string | null | undefined;
  size?: number;
  fallbackIcon?: React.ReactNode;
  fallbackBgColor?: string;
  sx?: SxProps<Theme>;
  onClick?: () => void;
}

// Check if an image URL is already cached in the browser
const isImageCached = (url: string): boolean => {
  const img = new Image();
  img.src = url;
  return img.complete && img.naturalWidth > 0;
};

const MAX_RETRIES = 2;
const RETRY_DELAY = 1500; // ms

/**
 * Avatar component that avoids showing colored background while image loads.
 * Shows a circular skeleton during loading to indicate content is coming.
 * Shows fallback icon with colored background when no image or on error.
 * When transitioning between URLs, keeps showing the previous image until the new one loads.
 * Retries failed image loads before giving up.
 */
export const PreloadedAvatar: React.FC<PreloadedAvatarProps> = ({
  src,
  size = 40,
  fallbackIcon,
  fallbackBgColor,
  sx,
  onClick,
}) => {
  // Track the last successfully loaded src to keep displaying during URL transitions
  const lastLoadedSrc = useRef<string | null>(null);

  // Check cache synchronously for instant display of cached images
  // undefined = still loading, null = no image (fallback), string = has image
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error'>(() => {
    if (src === undefined) return 'loading';
    if (src === null) return 'error';
    if (isImageCached(src)) {
      lastLoadedSrc.current = src;
      return 'loaded';
    }
    return 'loading';
  });

  // Reset state when src changes
  useEffect(() => {
    if (src === undefined) {
      setImageState('loading');
      return;
    }
    if (src === null) {
      setImageState('error');
      lastLoadedSrc.current = null;
      return;
    }
    if (isImageCached(src)) {
      lastLoadedSrc.current = src;
      setImageState('loaded');
      return;
    }

    // New URL not yet cached — keep showing old image if we have one
    if (!lastLoadedSrc.current) {
      setImageState('loading');
    }

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout>;

    const tryLoad = (attempt: number) => {
      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        lastLoadedSrc.current = src;
        setImageState('loaded');
      };
      img.onerror = () => {
        if (cancelled) return;
        if (attempt < MAX_RETRIES) {
          retryTimer = setTimeout(() => tryLoad(attempt + 1), RETRY_DELAY);
        } else if (!lastLoadedSrc.current) {
          setImageState('error');
        }
      };
      img.src = src;
    };

    tryLoad(0);

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [src]);

  const handleLoad = () => {
    if (src) lastLoadedSrc.current = src;
    setImageState('loaded');
  };

  const handleError = () => {
    if (!lastLoadedSrc.current) {
      setImageState('error');
    }
  };

  // Determine what to actually display
  const displaySrc = imageState === 'loaded'
    ? (src || lastLoadedSrc.current)
    : lastLoadedSrc.current;

  const showFallback = imageState === 'error' && !lastLoadedSrc.current;
  const showSkeleton = imageState === 'loading' && !lastLoadedSrc.current;

  // Show skeleton while waiting for image
  if (showSkeleton) {
    return (
      <Skeleton
        variant="circular"
        sx={{ width: size, height: size, ...sx }}
      />
    );
  }

  return (
    <Avatar
      src={displaySrc || undefined}
      onClick={onClick}
      imgProps={{
        onLoad: handleLoad,
        onError: handleError,
      }}
      sx={{
        width: size,
        height: size,
        bgcolor: showFallback ? (fallbackBgColor || 'primary.main') : 'transparent',
        ...sx,
      }}
    >
      {showFallback && fallbackIcon}
    </Avatar>
  );
};
